import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { HttpRetryFailureKind } from "@firm360/libs/api/http";
import { type Observable, catchError, throwError } from "rxjs";
import type { PendoProfile } from "../../pendo-profile.model";
import type { PendoProfileSource } from "../pendo-profile-source";
import { CurrentServerProfileSource } from "./current-server.pendo-profile-source";
import { LegacyServerProfileSource } from "./legacy-server.pendo-profile-source";

/**
 * The server's profile from its current endpoint, asked persistently.
 *
 * The legacy endpoint stands in, just as persistently, when the current one is
 * missing or unreadable, as on a backend that predates it. It's also asked
 * once, after the current one has given up on recurring failures, so a fault
 * in the current endpoint alone doesn't cost the session its profile for
 * long. A server that refuses the session or asks to be left alone isn't
 * asked through the legacy endpoint.
 */
@Injectable()
export class EitherServerProfileSource implements PendoProfileSource {
    private readonly _current = inject(CurrentServerProfileSource);
    private readonly _legacy = inject(LegacyServerProfileSource);

    contribute$(): Observable<PendoProfile> {
        return this._current.contribute$().pipe(
            catchError((error: unknown) => {
                if (isMissing(error)) return this._legacy.contribute$();
                if (HttpRetryFailureKind.of(error) === "blip") {
                    return this._legacy
                        .contributeOnce$()
                        .pipe(catchError(() => throwError(() => error)));
                }
                return throwError(() => error);
            }),
        );
    }
}

/** A failure retrying can't fix that isn't the session being refused. */
function isMissing(error: unknown): boolean {
    const refused =
        error instanceof HttpErrorResponse &&
        (error.status === 401 || error.status === 403);
    return !refused && HttpRetryFailureKind.of(error) === "permanent";
}
